/**
 * Topics Service for Client-Side Operations
 * Handles all topic CRUD operations using Supabase directly
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { Topic, InsertTopic } from "@shared/schema";
import { scheduleScopeEmbedding } from "@/services/scopeEmbeddingJobs";

class TopicsService {
    /**
     * Generate a simple unique ID
     */
    private generateSimpleUniqueId(): string {
        const timestamp = Date.now().toString(36);
        const randomPart = Math.random().toString(36).substring(2, 9);
        return timestamp + randomPart;
    }

    /**
     * Get current user ID from centralized session (single auth call on load)
     */
    private async getCurrentUserId(): Promise<string> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }
        const session = await getCurrentSession();
        if (!session?.user) {
            throw new Error("User not authenticated. Please log in.");
        }
        return session.user.id;
    }

    /**
     * Map database row to Topic (camelCase). Supports both workspace_id and workspaceId column names.
     */
    private mapToTopic(item: any): Topic {
        return {
            id: String(item.id),
            spaceId: item.space_id,
            workspaceId: item.workspace_id ?? item.workspaceId ?? undefined,
            name: item.name,
            description: item.description,
            color: item.color,
            icon: item.icon,
            order: item.order || "0",
            isLocked: item.is_locked === true || item.is_locked === "true",
            lockedAt: item.locked_at,
            createdByUserId: item.created_by_user_id ?? undefined,
            mergedFromSpaceId:
                item.merged_from_space_id != null && item.merged_from_space_id !== ""
                    ? String(item.merged_from_space_id)
                    : undefined,
            parentTopicId:
                item.parent_id != null && item.parent_id !== ""
                    ? String(item.parent_id)
                    : undefined,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
        } as Topic;
    }

    /**
     * Get all topics for a specific space (primary space_id + secondary links).
     */
    async getTopics(spaceId: string): Promise<Topic[]> {
        if (!spaceId) {
            throw new Error("Space ID is required");
        }

        const selectCols =
            "id, space_id, workspaceId, name, description, color, icon, order, is_locked, locked_at, created_by_user_id, merged_from_space_id, parent_id, created_at, updated_at";

        const { data, error } = await supabase
            .from("topics")
            .select(selectCols)
            .eq("space_id", spaceId)
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Error fetching topics:", error);
            throw new Error(`Failed to fetch topics: ${error.message}`);
        }

        const primaryList = (data || []).map((item: any) => this.mapToTopic(item));
        const byId = new Map(primaryList.map((t) => [t.id, t]));

        const { data: linkRows, error: linkErr } = await supabase
            .from("topic_secondary_space_links")
            .select("topic_id")
            .eq("space_id", spaceId);

        if (linkErr) {
            console.warn("topic_secondary_space_links:", linkErr.message);
            return primaryList;
        }

        const extraIds = [...new Set((linkRows || []).map((r: { topic_id: string }) => r.topic_id).filter(Boolean))].filter(
            (id) => !byId.has(id)
        );

        if (extraIds.length === 0) {
            return primaryList;
        }

        const { data: extraRows, error: extraErr } = await supabase
            .from("topics")
            .select(selectCols)
            .in("id", extraIds);

        if (extraErr) {
            console.warn("Error fetching secondary-linked topics:", extraErr.message);
            return primaryList;
        }

        for (const item of extraRows || []) {
            byId.set(String(item.id), this.mapToTopic(item));
        }

        return [...byId.values()].sort((a, b) => {
            const aT = new Date(a.createdAt ?? 0).getTime();
            const bT = new Date(b.createdAt ?? 0).getTime();
            return bT - aT;
        });
    }

    /** Primary space + secondary link spaces for a topic. */
    async getTopicLinkedSpaceIds(topicId: string): Promise<string[]> {
        const topic = await this.getTopic(topicId);
        if (!topic) {
            throw new Error("Topic not found");
        }
        const secondary = await this.getTopicSecondarySpaceIds(topicId);
        return [...new Set([topic.spaceId, ...secondary])];
    }

    private async getTopicSecondarySpaceIds(topicId: string): Promise<string[]> {
        const { data, error } = await supabase
            .from("topic_secondary_space_links")
            .select("space_id")
            .eq("topic_id", topicId);

        if (error) {
            if (error.code === "42P01" || String(error.message || "").includes("does not exist")) {
                return [];
            }
            console.warn("getTopicSecondarySpaceIds:", error.message);
            return [];
        }
        return (data || []).map((r: { space_id: string }) => String(r.space_id)).filter(Boolean);
    }

    private async assertUserOwnsMastermindSpace(userId: string, mastermindSpaceId: string): Promise<void> {
        const { data, error } = await supabase
            .from("mastermind")
            .select("id")
            .eq("id", mastermindSpaceId)
            .eq("user_id", userId)
            .maybeSingle();

        if (error || !data) {
            throw new Error("You don't have permission to link this topic to that space");
        }
    }

    /**
     * Sync secondary space links. Primary home stays topics.space_id (use moveTopicToSpace to change it).
     */
    async syncTopicLinkedSpaces(topicId: string, selectedSpaceIds: string[]): Promise<void> {
        const userId = await this.getCurrentUserId();
        const topic = await this.getTopic(topicId);
        if (!topic) {
            throw new Error("Topic not found");
        }

        const primary = topic.spaceId;
        const deduped = [...new Set(selectedSpaceIds.filter(Boolean))];
        if (deduped.length === 0) {
            throw new Error("Select at least one space");
        }
        if (!deduped.includes(primary)) {
            throw new Error(
                "The topic's home space must stay selected. Use Move topic to change its home space."
            );
        }

        const secondary = deduped.filter((id) => id !== primary);

        await this.assertUserOwnsMastermindSpace(userId, primary);
        for (const sid of secondary) {
            await this.assertUserOwnsMastermindSpace(userId, sid);
        }

        const { error: delErr } = await supabase.from("topic_secondary_space_links").delete().eq("topic_id", topicId);
        if (delErr) {
            throw new Error(delErr.message || "Failed to update topic space links");
        }

        if (secondary.length === 0) {
            return;
        }

        const rows = secondary.map((space_id) => ({ topic_id: topicId, space_id }));
        const { error: insErr } = await supabase.from("topic_secondary_space_links").insert(rows);
        if (insErr) {
            throw new Error(insErr.message || "Failed to add topic space links");
        }
    }

    /**
     * Get all topics for the current user (owned via spaces + shared with user).
     * @param workspaceId - When undefined or "personal": no workspace filter (all topics). When null: personal only (workspace_id IS NULL). When set: topics for that workspace.
     */
    async getAllTopics(workspaceId?: string | null): Promise<Topic[]> {
        const userId = await this.getCurrentUserId();

        // Treat "personal" string as undefined so Personal scope shows all topics (same as folders)
        const resolved =
            workspaceId === "personal" || workspaceId === "" ? undefined : workspaceId;

        const query = supabase
            .from("topics")
            .select("*")
            .order("created_at", { ascending: false });
       /* if (resolved !== undefined) {
            if (resolved != null && resolved !== "") {
                query = query.is("workspaceId", null);
                // query = query.eq("workspaceId", resolved);
            } else {
                query = query.is("workspaceId", null);
            }
        }*/
        const { data, error } = await query;

        if (error) {
            console.error("Error fetching all topics:", error);
            throw new Error(`Failed to fetch topics: ${error.message}`);
        }

        const ownedMap = new Map<string, Topic>();
        (data || []).forEach((item: any) => {
            ownedMap.set(item.id, this.mapToTopic(item));
        });

        const { data: shareAccess } = await supabase
            .from("topic_share_access")
            .select("share_id")
            .eq("user_id", userId)
            .eq("status", "accepted");
        const shareIds = shareAccess ? shareAccess.map((a: any) => a.share_id) : [];
        if (shareIds.length > 0) {
            let sharedQuery = supabase
                .from("topic_shares")
                .select("topic_id")
                .in("id", shareIds)
                .eq("is_active", "true");
            const { data: shares } = await sharedQuery;
            const sharedTopicIds = shares ? [...new Set(shares.map((s: any) => s.topic_id))] : [];
            if (sharedTopicIds.length > 0) {
                let topicsQuery = supabase
                    .from("topics")
                    .select("id, space_id, workspaceId, name, description, color, icon, order, is_locked, locked_at, created_by_user_id, merged_from_space_id, created_at, updated_at")
                    .in("id", sharedTopicIds);
                if (resolved !== undefined) {
                    if (resolved != null && resolved !== "") {
                        topicsQuery = topicsQuery.eq("workspaceId", resolved);
                    } else {
                        topicsQuery = topicsQuery.is("workspaceId", null);
                    }
                }
                const { data: sharedRows } = await topicsQuery;
                (sharedRows || []).forEach((item: any) => {
                    if (!ownedMap.has(item.id)) {
                        ownedMap.set(item.id, this.mapToTopic(item));
                    }
                });
            }
        }

        return Array.from(ownedMap.values()).sort((a, b) => {
            const aVal = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
            const bVal = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
            return bVal - aVal;
        });
    }

    /**
     * Get a single topic by ID (user must own the space or have accepted share access)
     */
    async getTopic(id: string): Promise<Topic | null> {
        const userId = await this.getCurrentUserId();

        const { data: topicRow, error: topicError } = await supabase
            .from("topics")
            .select("id, space_id, name, description, color, icon, order, is_locked, locked_at, created_by_user_id, merged_from_space_id, created_at, updated_at")
            .eq("id", id)
            .single();

        if (topicError || !topicRow) {
            if (topicError?.code === "PGRST116") return null;
            console.error("Error fetching topic:", topicError);
            throw new Error(`Failed to fetch topic: ${topicError?.message}`);
        }

        const { data: space } = await supabase
            .from("mastermind")
            .select("user_id")
            .eq("id", topicRow.space_id)
            .eq("user_id", userId)
            .maybeSingle();

        if (space) return this.mapToTopic(topicRow);

        const { data: shares } = await supabase
            .from("topic_shares")
            .select("id")
            .eq("topic_id", id)
            .eq("is_active", "true");
        if (!shares || shares.length === 0) return null;

        const shareIds = shares.map((s: any) => s.id);
        const { data: access } = await supabase
            .from("topic_share_access")
            .select("id")
            .eq("user_id", userId)
            .eq("status", "accepted")
            .in("share_id", shareIds)
            .maybeSingle();

        if (!access) return null;
        return this.mapToTopic(topicRow);
    }

    /**
     * Create a new topic
     */
    async createTopic(topicData: InsertTopic & { icon?: string; tags?: string[]; parentTopicId?: string }): Promise<Topic> {
        if (!topicData.name || !topicData.name.trim()) {
            throw new Error("Name is required");
        }

        if (!topicData.spaceId) {
            throw new Error("Space ID is required");
        }

        const userId = await this.getCurrentUserId();

        const { spaceSharingService } = await import("@/services/spaceSharingService");
        const { canCreateTopicsInSpace } = await import("@/services/sharedSpacePermissionService");
        const spaceRole = await spaceSharingService.getCurrentUserSpaceRole(topicData.spaceId);
        if (!canCreateTopicsInSpace(spaceRole)) {
            throw new Error(
                "You don't have permission to create topics in this space. Viewers can browse existing topics only."
            );
        }

        const { data: spaceRow } = await supabase
            .from("mastermind")
            .select("workspace_id")
            .eq("id", topicData.spaceId)
            .maybeSingle();
        const workspaceId =
            (spaceRow as { workspace_id?: string | null } | null)?.workspace_id ?? null;

        const topicToInsert: any = {
            space_id: topicData.spaceId,
            name: topicData.name.trim(),
            description: topicData.description || null,
            color: topicData.color || null,
            order: topicData.order || "0",
            created_by_user_id: userId,
        };
        if (workspaceId) {
            topicToInsert.workspaceId = workspaceId;
        }

        // Store icon and tags if provided
        if (topicData.icon) topicToInsert.icon = topicData.icon;
        if (topicData.tags) topicToInsert.tags = topicData.tags;
        if (topicData.parentTopicId) topicToInsert.parent_id = topicData.parentTopicId;

        console.log("Creating topic:", topicToInsert);

        const { data, error } = await supabase
            .from("topics")
            .insert(topicToInsert)
            .select()
            .single();

        if (error) {
            console.error("Error creating topic:", error);
            throw new Error(`Failed to create topic: ${error.message}`);
        }

        if (!data) {
            throw new Error("Failed to create topic: No data returned");
        }

        scheduleScopeEmbedding("topic", String(data.id));
        return this.mapToTopic(data);
    }

    /**
     * Update an existing topic
     * @param spaceContextId - When editing in a shared space as editor (not topic creator), pass space id for permission RPC.
     */
    async updateTopic(
        id: string,
        updates: Partial<InsertTopic> & { icon?: string; tags?: string[] },
        spaceContextId?: string | null
    ): Promise<Topic | null> {
        if (spaceContextId) {
            const pUpdates: Record<string, unknown> = {};
            if (updates.name !== undefined) pUpdates.name = updates.name;
            if (updates.description !== undefined) pUpdates.description = updates.description;
            if (updates.color !== undefined) pUpdates.color = updates.color;
            if (updates.icon !== undefined) pUpdates.icon = updates.icon;
            if (updates.order !== undefined) pUpdates.order = updates.order;

            const { data: rpcData, error: rpcErr } = await supabase.rpc("shared_update_topic", {
                p_id: id,
                p_space_id: spaceContextId,
                p_updates: pUpdates as any,
            });
            if (rpcErr) {
                throw new Error(rpcErr.message || "Failed to update topic (shared)");
            }
            if (!rpcData) return null;
            const raw = typeof rpcData === "object" && rpcData !== null ? rpcData : JSON.parse(String(rpcData));
            if (updates.name !== undefined || updates.description !== undefined) {
                scheduleScopeEmbedding("topic", id);
            }
            return this.mapToTopic(raw);
        }

        const updateData: any = {
            updated_at: new Date().toISOString(),
        };

        if (updates.name !== undefined) {
            if (!updates.name || !updates.name.trim()) {
                throw new Error("Name cannot be empty");
            }
            updateData.name = updates.name.trim();
        }
        if (updates.description !== undefined) updateData.description = updates.description;
        if (updates.color !== undefined) updateData.color = updates.color;
        if (updates.order !== undefined) updateData.order = updates.order;
        if (updates.spaceId !== undefined) {
            updateData.space_id = updates.spaceId;
            const { data: spaceRow } = await supabase
                .from("mastermind")
                .select("workspace_id")
                .eq("id", updates.spaceId)
                .maybeSingle();
            const wsId = (spaceRow as any)?.workspace_id ?? null;
            updateData.workspaceId = wsId != null && wsId !== "" ? wsId : null;
        }

        // Update icon and tags if provided
        if (updates.icon !== undefined) updateData.icon = updates.icon;
        if (updates.tags !== undefined) updateData.tags = updates.tags;

        const { data, error } = await supabase
            .from("topics")
            .update(updateData)
            .eq("id", id)
            .select()
            .single();

        if (error) {
            if (error.code === "PGRST116") {
                // No rows returned
                return null;
            }
            console.error("Error updating topic:", error);
            throw new Error(`Failed to update topic: ${error.message}`);
        }

        if (!data) return null;

        if (updates.name !== undefined || updates.description !== undefined) {
            scheduleScopeEmbedding("topic", id);
        }
        return this.mapToTopic(data);
    }

    /**
     * Collect all descendant topic ids (subtopics, sub-subtopics, ...) of a topic.
     * There is no DB-level FK/cascade on topics.parent_id, so deletion cascades here instead.
     */
    private async getDescendantTopicIds(rootId: string): Promise<string[]> {
        const allIds: string[] = [];
        let frontier = [rootId];
        while (frontier.length > 0) {
            const { data, error } = await supabase
                .from("topics")
                .select("id")
                .in("parent_id", frontier);
            if (error) {
                console.warn("Could not fetch subtopics for cascade delete:", error.message);
                break;
            }
            const childIds = (data || []).map((r: { id: string | number }) => String(r.id));
            if (childIds.length === 0) break;
            allIds.push(...childIds);
            frontier = childIds;
        }
        return allIds;
    }

    /**
     * Delete a topic
     * @param spaceContextId - When deleting as space owner (not creator), pass space id for RPC.
     */
    async deleteTopic(id: string, spaceContextId?: string | null): Promise<boolean> {
        const descendantIds = await this.getDescendantTopicIds(id);

        const { error: linksErr } = await supabase
            .from("topic_secondary_space_links")
            .delete()
            .eq("topic_id", id);
        if (linksErr) {
            console.warn("Could not delete topic secondary space links:", linksErr.message);
        }

        if (descendantIds.length > 0) {
            const { error: descLinksErr } = await supabase
                .from("topic_secondary_space_links")
                .delete()
                .in("topic_id", descendantIds);
            if (descLinksErr) {
                console.warn("Could not delete subtopic secondary space links:", descLinksErr.message);
            }

            const { error: descErr } = await supabase
                .from("topics")
                .delete()
                .in("id", descendantIds);
            if (descErr) {
                console.error("Error deleting subtopics:", descErr);
                throw new Error(`Failed to delete subtopics: ${descErr.message}`);
            }
        }

        if (spaceContextId) {
            const { error: rpcErr } = await supabase.rpc("shared_delete_topic", {
                p_id: id,
                p_space_id: spaceContextId,
            });
            if (rpcErr) {
                throw new Error(rpcErr.message || "Failed to delete topic");
            }
            return true;
        }

        const { error } = await supabase
            .from("topics")
            .delete()
            .eq("id", id);

        if (error) {
            console.error("Error deleting topic:", error);
            throw new Error(`Failed to delete topic: ${error.message}`);
        }

        return true;
    }

    /**
     * Get subtopics for a topic
     */
    async getSubtopics(topicId: string): Promise<any[]> {
        const { data, error } = await supabase
            .from("subtopics")
            .select("id, topic_id, name, description, order, created_at, updated_at")
            .eq("topic_id", topicId)
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Error fetching subtopics:", error);
            throw new Error(`Failed to fetch subtopics: ${error.message}`);
        }

        return (data || []).map((item: any) => ({
            id: item.id,
            topicId: item.topic_id,
            name: item.name,
            description: item.description,
            order: item.order,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
        }));
    }

    /**
     * Get threads for a topic
     */
    async getTopicThreads(topicId: string): Promise<any[]> {
        const userId = await this.getCurrentUserId();
        
        const { data: threads, error: threadsError } = await supabase
            .from("chat-threads")
            .select("id, userId, title, topic, topicId, topicIds, tags, messages, linkedSpaceIds, agentId, contextMode, status, createdAt, updatedAt")
            .eq("userId", userId)
            // Match legacy single topicId column OR the newer topicIds jsonb array
            .or(`topicId.eq.${topicId},topicIds.cs.["${topicId}"]`)
            .order("updatedAt", { ascending: false });

        if (threadsError) {
            console.error("Error fetching chat threads:", threadsError);
            throw new Error(`Failed to fetch chat threads: ${threadsError.message}`);
        }

        // Table uses camelCase columns directly
        return (threads || []).map((thread: any) => ({
            id: thread.id,
            userId: thread.userId,
            title: thread.title,
            topic: thread.topic,
            topicId: (Array.isArray(thread.topicIds) && thread.topicIds.length > 0)
                ? String(thread.topicIds[0])
                : thread.topicId,
            topicIds: Array.isArray(thread.topicIds) ? thread.topicIds : undefined,
            tags: thread.tags,
            messages: thread.messages,
            linkedSpaceIds: thread.linkedSpaceIds,
            agentId: thread.agentId,
            contextMode: thread.contextMode,
            status: thread.status,
            createdAt: thread.createdAt,
            updatedAt: thread.updatedAt,
        }));
    }

    /**
     * Lock or unlock a topic
     */
    async lockTopic(id: string, lock: boolean): Promise<Topic | null> {
        const updateData: Record<string, unknown> = {
            is_locked: lock,
            locked_at: lock ? new Date().toISOString() : null,
            updated_at: new Date().toISOString(),
        };

        const { data, error } = await supabase
            .from("topics")
            .update(updateData)
            .eq("id", id)
            .select()
            .single();

        if (error) {
            console.error("Error locking/unlocking topic:", error);
            throw new Error(`Failed to update lock status: ${error.message}`);
        }

        if (!data) return null;
        return this.mapToTopic(data);
    }

    /**
     * Move a topic to a different space
     * Verifies ownership of both topic (via its space) and target space
     */
    async moveTopicToSpace(topicId: string, targetSpaceId: string): Promise<Topic | null> {
        const userId = await this.getCurrentUserId();
        
        // First verify the topic exists and get its current space
        const topic = await this.getTopic(topicId);
        if (!topic) {
            throw new Error("Topic not found");
        }

        // Verify user owns the current space (topic's space)
        const { data: currentSpace, error: currentSpaceError } = await supabase
            .from("mastermind")
            .select("id, user_id")
            .eq("id", topic.spaceId)
            .eq("user_id", userId)
            .single();

        if (currentSpaceError || !currentSpace) {
            throw new Error("You don't have permission to move this topic");
        }

        // Verify user owns the target space and get its workspace_id
        const { data: targetSpace, error: targetSpaceError } = await supabase
            .from("mastermind")
            .select("id, user_id, workspace_id")
            .eq("id", targetSpaceId)
            .eq("user_id", userId)
            .single();

        if (targetSpaceError || !targetSpace) {
            throw new Error("You don't have permission to move to the target space");
        }

        const updateData: any = {
            space_id: targetSpaceId,
            updated_at: new Date().toISOString(),
        };
        const targetWorkspaceId = (targetSpace as any).workspace_id ?? null;
        if (targetWorkspaceId != null && targetWorkspaceId !== "") {
            updateData.workspaceId = targetWorkspaceId;
        } else {
            updateData.workspaceId = null;
        }

        const { data, error } = await supabase
            .from("topics")
            .update(updateData)
            .eq("id", topicId)
            .select()
            .single();

        if (error) {
            console.error("Error moving topic to space:", error);
            throw new Error(`Failed to move topic: ${error.message}`);
        }

        if (!data) return null;

        const { error: linkDelErr } = await supabase
            .from("topic_secondary_space_links")
            .delete()
            .eq("topic_id", topicId)
            .eq("space_id", targetSpaceId);
        if (linkDelErr) {
            console.warn("topic_secondary_space_links cleanup after move:", linkDelErr.message);
        }

        return this.mapToTopic(data);
    }
}

/**
 * Create and export a singleton instance
 */
export const topicsService = new TopicsService();

